(() => {
  'use strict';

  /** These preview pairings are owner-supplied, not claims about a health outcome. */
  const PRESETS = Object.freeze({
    coffee: Object.freeze({ color: 4, brightness: 4 }),
    book: Object.freeze({ color: 3, brightness: 4 }),
    gaming: Object.freeze({ color: 2, brightness: 4 }),
    computer: Object.freeze({ color: 5, brightness: 4 }),
    sleep: Object.freeze({ color: 1, brightness: 1 }),
  });
  const TIMER_MINUTES = Object.freeze([0, 30, 60, 90, 120]);
  const isLevel = (value) => Number.isInteger(value) && value >= 1 && value <= 5;

  /** Pure, clock-injected state for the 25 visual combinations and the off state. */
  class LightLabState {
    /** @param {() => number} [clock] Returns an absolute timestamp in milliseconds. */
    constructor(clock = Date.now) {
      this.clock = clock;
      this.color = 5;
      this.brightness = 4;
      this.power = true;
      this.deadline = null;
      this.timerMinutes = 0;
    }

    /** @param {number} color A whole-number preview color from 1 through 5. */
    setColor(color) {
      if (!isLevel(color)) return false;
      this.color = color;
      this.power = true;
      return true;
    }

    /** @param {number} brightness A whole-number preview brightness from 1 through 5. */
    setBrightness(brightness) {
      if (!isLevel(brightness)) return false;
      this.brightness = brightness;
      this.power = true;
      return true;
    }

    /** @param {string} name A key in the owner-supplied preset table. */
    applyPreset(name) {
      if (!Object.hasOwn(PRESETS, name)) return false;
      this.color = PRESETS[name].color;
      this.brightness = PRESETS[name].brightness;
      this.power = true;
      return true;
    }

    /** @param {boolean} power Whether the browser preview should be on. */
    setPower(power) {
      this.power = Boolean(power);
      if (!this.power) this.setTimer(0);
    }

    /** @param {number} minutes Zero cancels without turning the preview off. */
    setTimer(minutes) {
      if (!TIMER_MINUTES.includes(minutes)) return false;
      this.timerMinutes = minutes;
      this.deadline = minutes === 0 ? null : this.clock() + minutes * 60_000;
      if (minutes > 0) this.power = true;
      return true;
    }

    /** @returns {number} Actual remaining seconds, independent of skipped browser ticks. */
    get remainingSeconds() {
      if (this.deadline === null) return 0;
      return Math.max(0, Math.ceil((this.deadline - this.clock()) / 1000));
    }

    /** @returns {boolean} True only on the transition from a running timer to expired. */
    tick() {
      if (this.deadline === null || this.clock() < this.deadline) return false;
      this.setPower(false);
      return true;
    }

    /** @returns {string|null} The matching preset, or null for a custom/off combination. */
    get activePreset() {
      if (!this.power) return null;
      for (const [name, values] of Object.entries(PRESETS)) {
        if (this.color === values.color && this.brightness === values.brightness) return name;
      }
      return null;
    }

    /** @returns {string} Key for one of the 25 fixed on-state images or the fixed off image. */
    get assetKey() {
      return this.power ? `${this.color}-${this.brightness}` : 'off';
    }
  }

  /** @param {number} seconds @returns {string} Quiet, visible H:MM:SS / M:SS readout. */
  function formatCountdown(seconds) {
    const safeSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainder = String(safeSeconds % 60).padStart(2, '0');
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${remainder}`;
    return `${minutes}:${remainder}`;
  }

  // Makes the same production state implementation available to dependency-free unit tests.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { LightLabState, PRESETS, TIMER_MINUTES, formatCountdown };
  }
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('concept-light-lab')) return;

  class ConceptLightLab extends HTMLElement {
    #controller;
    #timer;

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.state ??= new LightLabState();
      this.controls = this.querySelector('[data-lab-controls]');
      this.powerButton = this.querySelector('[data-lab-power]');
      this.stateText = this.querySelector('[data-lab-state]');
      this.scene = this.querySelector('[data-lab-scene]');
      this.previewImage = this.querySelector('[data-lab-image]');
      const assetTemplate = this.querySelector('[data-lab-assets]');
      this.countdown = this.querySelector('[data-lab-countdown]');
      this.announcement = this.querySelector('[data-lab-announcement]');
      if (!this.controls || !this.powerButton || !this.stateText || !this.scene || !this.previewImage || !assetTemplate) return;

      this.assetUrls = new Map(
        [...assetTemplate.content.querySelectorAll('[data-lab-asset-key]')]
          .map((item) => [item.dataset.labAssetKey, item.dataset.labAssetUrl]),
      );

      this.colorButtons = [...this.querySelectorAll('[data-lab-color]')];
      this.brightnessButtons = [...this.querySelectorAll('[data-lab-brightness]')];
      this.presetButtons = [...this.querySelectorAll('[data-lab-preset]')];
      this.timerButtons = [...this.querySelectorAll('[data-lab-timer]')];
      this.addEventListener('click', (event) => this.#handleClick(event), { signal: this.#controller.signal });
      document.addEventListener('visibilitychange', () => this.#tick(), { signal: this.#controller.signal });
      window.addEventListener('focus', () => this.#tick(), { signal: this.#controller.signal });

      this.controls.hidden = false;
      this.powerButton.hidden = false;
      const fallback = this.querySelector('[data-lab-fallback]');
      if (fallback) fallback.hidden = true;
      this.setAttribute('data-enhanced', '');
      this.#tick();
      this.#render();
      this.#schedule();
      this.#preparePreload();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.assetObserver?.disconnect();
      window.clearTimeout(this.#timer);
    }

    /** @param {MouseEvent} event Native buttons also support Enter and Space. */
    #handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      this.#tick();
      let message = null;

      if (button.hasAttribute('data-lab-color')) {
        if (!this.state.setColor(Number(button.dataset.labColor))) return;
      } else if (button.hasAttribute('data-lab-brightness')) {
        if (!this.state.setBrightness(Number(button.dataset.labBrightness))) return;
      } else if (button.hasAttribute('data-lab-preset')) {
        if (!this.state.applyPreset(button.dataset.labPreset)) return;
      } else if (button.hasAttribute('data-lab-timer')) {
        const minutes = Number(button.dataset.labTimer);
        if (!this.state.setTimer(minutes)) return;
        message = minutes === 0
          ? this.dataset.labelTimerCancelled
          : this.dataset.labelTimerSet.replace('__MINUTES__', String(minutes));
      } else if (button.hasAttribute('data-lab-power')) {
        this.state.setPower(!this.state.power);
      } else {
        return;
      }

      this.#render();
      this.#announce(message ?? this.stateText.textContent);
      this.#schedule();
    }

    /** @returns {string} Localized visible color name, never an asset filename. */
    #colorName() {
      return this.colorButtons.find((button) => Number(button.dataset.labColor) === this.state.color)?.dataset.labColorName ?? '';
    }

    /** @param {string} template @returns {string} Text-only localized state interpolation. */
    #stateLabel(template) {
      return (template ?? '').replace('__COLOR__', this.#colorName()).replace('__LEVEL__', String(this.state.brightness));
    }

    #render() {
      this.dataset.power = this.state.power ? 'on' : 'off';
      this.dataset.color = String(this.state.color);
      this.dataset.brightness = String(this.state.brightness);
      this.#showAsset(this.state.assetKey);
      this.stateText.textContent = this.state.power ? this.#stateLabel(this.dataset.labelState) : this.dataset.labelOff;
      this.scene.setAttribute('aria-label', this.state.power ? this.#stateLabel(this.dataset.labelScene) : this.dataset.labelOff);
      this.powerButton.setAttribute('aria-pressed', String(this.state.power));
      const powerState = this.querySelector('[data-lab-power-state]');
      if (powerState) powerState.textContent = this.state.power ? this.dataset.labelOn : this.dataset.labelPowerOff;

      for (const button of this.colorButtons) button.setAttribute('aria-pressed', String(Number(button.dataset.labColor) === this.state.color));
      for (const button of this.brightnessButtons) button.setAttribute('aria-pressed', String(Number(button.dataset.labBrightness) === this.state.brightness));
      for (const button of this.presetButtons) button.setAttribute('aria-pressed', String(button.dataset.labPreset === this.state.activePreset));
      for (const button of this.timerButtons) button.setAttribute('aria-pressed', String(Number(button.dataset.labTimer) === this.state.timerMinutes));
      this.#renderCountdown();
    }

    /** @param {string} key */
    #showAsset(key) {
      const url = this.assetUrls.get(key);
      if (!url || this.previewImage.dataset.labAssetKey === key) return;
      this.previewImage.src = url;
      this.previewImage.dataset.labAssetKey = key;
    }

    #preparePreload() {
      const preload = () => {
        this.assetObserver?.disconnect();
        this.preloads = [...this.assetUrls.values()].map((url) => {
          const image = new window.Image();
          image.decoding = 'async';
          image.src = url;
          return image;
        });
      };
      if (!('IntersectionObserver' in window)) {
        preload();
        return;
      }
      this.assetObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) preload();
      }, { rootMargin: '800px 0px' });
      this.assetObserver.observe(this);
    }

    #renderCountdown() {
      if (!this.countdown) return;
      const text = this.state.deadline === null ? this.dataset.labelTimerOff : formatCountdown(this.state.remainingSeconds);
      if (this.countdown.textContent !== text) this.countdown.textContent = text;
    }

    #tick() {
      if (this.state.tick()) {
        window.clearTimeout(this.#timer);
        this.#render();
        this.#announce(this.dataset.labelTimerEnded);
        return;
      }
      this.#renderCountdown();
    }

    #schedule() {
      window.clearTimeout(this.#timer);
      if (this.state.deadline === null || !this.isConnected) return;
      this.#timer = window.setTimeout(() => {
        this.#tick();
        this.#schedule();
      }, Math.min(1000, Math.max(1, this.state.deadline - this.state.clock())));
    }

    /** @param {string} message Only user actions and expiration enter the live region. */
    #announce(message) {
      if (this.announcement) this.announcement.textContent = message;
    }
  }

  customElements.define('concept-light-lab', ConceptLightLab);
})();
